import { useCallback, useContext, useMemo } from "react";

import { formatResolvedPriceString } from "@/components/care/utils";
import { CartContext } from "@/contexts/cartContext";
import { useLatestReadyTestContext } from "@/contexts/latestReadyTestContext";
// services
import { cartService } from "@/services/cart";
// types
import { AddCartItemPayload, CartItem } from "@/types/cart";
import { BrazeEvents } from "@/utils/analytics/braze/constants";
import { trackEvent } from "@/utils/analytics/braze/utils";
import {
  sendTreatmentCartClickedAddToCart,
  sendTreatmentCartClickedRemoveFromCart,
} from "@/utils/analytics/customEventTracking";

export const usePanelCart = () => {
  const {
    cart,
    loading: isLoading,
    setLoading: setIsLoading,
    setCart,
    showCart,
    setShowCart,
    refetchCart,
  } = useContext(CartContext);
  const { vaginalTest } = useLatestReadyTestContext();

  const baseCartAnalyticsArgs = useMemo(() => {
    const cartIsEmpty = !cart || cart.line_items.length === 0;
    const idRegex = /[A-Za-z\d]{32,100}/g;

    const pageNameExcludingIDs = window.location.pathname?.replace(
      idRegex,
      "uuid"
    );

    return {
      testHash: vaginalTest?.hash,
      consultId: vaginalTest?.latest_vaginitis_consult?.uid,
      careEligible: vaginalTest?.eligible_for_care,
      aLaCareEligible: vaginalTest?.eligible_for_a_la_care,
      // original data type is an array, keeping it as so until we refactor
      eligibleTreatmentPathways: [vaginalTest?.recommended_program_slug],
      selectedTreatments: cart?.line_items
        .filter((item) => item.item_type === "individual-treatment")
        .map((item) => item.treatment_product.slug),
      selectedProducts: cart?.line_items
        .filter((item) => item.item_type === "product")
        .map((item) => item.ecomm_product?.sku),
      selectedBundleTreatments: cart?.line_items
        .filter((item) => item.item_type === "bundle-treatment")
        .map((item) => item.treatment_product.slug),
      totalInCart: cart ? formatResolvedPriceString(cart.subtotal) : 0,
      containsSub: cart?.line_items.some((item) => item.is_subscription),
      page: pageNameExcludingIDs,
      emptyCart: cartIsEmpty,
      bvSubtypeSlug:
        vaginalTest?.result_infection_state?.bv_subtype_slug || null,
      hasBvSubtype:
        vaginalTest?.result_infection_state?.has_bv_subtype || false,
    };
  }, [vaginalTest, cart]);

  const addToCart = async (items: AddCartItemPayload[]) => {
    setIsLoading(true);
    // if not cart, create cart
    let updatedCart = cart;
    if (!cart) {
      updatedCart = await cartService.getOrCreateCart();
    }

    if (!updatedCart) {
      const errorMessage = "Error creating cart";
      console.error(errorMessage);
      throw new Error(errorMessage);
    }

    const newIndividualTreatments = items
      .filter((item) => item.item_type === "individual-treatment")
      .map((item) => item.treatment_product?.slug)
      .filter(Boolean);

    const newBundleTreatments = items
      .filter((item) => item.item_type === "bundle-treatment")
      .map((item) => item.treatment_product?.slug)
      .filter(Boolean);

    const currentSelectedTreatments =
      baseCartAnalyticsArgs.selectedTreatments || [];
    const currentSelectedBundleTreatments =
      baseCartAnalyticsArgs.selectedBundleTreatments || [];

    const properties = {
      addedTreatments: items
        .filter((item) => item.item_type !== "product")
        .map((item) => item.treatment_product?.slug),
      addedBundle: items.some((item) => item.item_type === "bundle-treatment"),
      addedUpsellSkus: items
        .filter((item) => item.item_type === "product")
        .map((item) => item.ecomm_product?.sku),
      ...baseCartAnalyticsArgs,
      selectedTreatments: [
        ...currentSelectedTreatments,
        ...newIndividualTreatments,
      ],
      selectedBundleTreatments: [
        ...currentSelectedBundleTreatments,
        ...newBundleTreatments,
      ],
    };

    sendTreatmentCartClickedAddToCart(properties);
    trackEvent(BrazeEvents.CARE_ADD_TO_CART, properties);

    // add items to cart; a refused add (400) must still clear the loading state
    try {
      updatedCart = await cartService.addItemsToCart(updatedCart.id, items);
      if (updatedCart) {
        setCart(updatedCart);
      }
      setShowCart(true);
    } finally {
      setIsLoading(false);
    }
  };

  const removeFromCart = async (items: CartItem[]) => {
    setIsLoading(true);
    // if not cart, then there's nothing to remove
    if (!cart) {
      return;
    }
    const removedTreatments = items
      .filter((item) => item.item_type !== "product")
      .map((item) => item.treatment_product?.slug);

    sendTreatmentCartClickedRemoveFromCart({
      removedTreatments,
      removedBundle: items.some(
        (item) => item.item_type === "bundle-treatment"
      ),
      removedUpsellSkus: items
        .filter((item) => item.item_type === "product")
        .map((item) => item.ecomm_product?.sku),
      ...baseCartAnalyticsArgs,
    });
    const updatedCart = await cartService.removeItemsFromCart(
      cart.id,
      items.map((item) => item.id)
    );
    setCart(updatedCart);
    setIsLoading(false);
  };

  const checkoutOnCart = async (consultId: string, checkoutSource?: string) => {
    if (!cart) {
      const errorMessage = "Error checking out: cart does not exist";
      console.error(errorMessage);
      throw new Error(errorMessage);
    }

    // create a util for this
    const checkoutData = await cartService.createCartCheckout(
      cart.id,
      consultId,
      undefined,
      checkoutSource
    );
    const checkoutUrl = checkoutData.checkout_url;
    trackEvent(BrazeEvents.CARE_CREATE_CHECKOUT, {
      ...baseCartAnalyticsArgs,
      purchaseType: cart.purchase_type,
    });
    return checkoutUrl;
  };

  const getIfBundleInCart = () => {
    return cart?.line_items.some(
      (item) => item.item_type === "bundle-treatment"
    );
  };

  const getIfALaCareTreatmentInCart = (treatmentSlug: string) => {
    return cart?.line_items.some(
      (item) =>
        item.item_type === "individual-treatment" &&
        item.treatment_product?.slug === treatmentSlug
    );
  };

  const getCartItemByTreatmentSlug = useCallback(
    (treatmentSlug: string) => {
      return cart?.line_items.find(
        (item) => item.treatment_product?.slug === treatmentSlug
      );
    },
    [cart]
  );

  const getCartItemByEcommProductSku = useCallback(
    (sku: string) => {
      return cart?.line_items.find((item) => item.ecomm_product?.sku === sku);
    },
    [cart]
  );

  return {
    cart,
    isLoading,
    addToCart, // function to add items to cart. Cart opens automatically after adding items
    removeFromCart, // function to remove items from cart
    checkoutOnCart, // function to checkout on cart, returns checkout URL
    getIfBundleInCart, // function to check if a bundle is in the cart
    getIfALaCareTreatmentInCart, // function to check if a specific treatment is in the cart
    getCartItemByTreatmentSlug, // function to get a cart item by treatment slug
    getCartItemByEcommProductSku, // function to get a cart item by ecomm product SKU
    showCart,
    setShowCart, // function to set whether cart is shown
    vaginalTest,
    baseCartAnalyticsArgs,
    refetchCart, // function to refetch cart
  };
};
